import React, { useContext } from 'react';
import styled from 'styled-components';
import { isEmpty } from 'lodash';
import { connect } from 'react-redux';

import type { IMonth } from '@lt/components/components/MonthSegments/Month';
import { MonthSegments } from '@lt/components';

// Components:
// import { pushExploreLinkClicked } from 'src/routes/SeoApp/utils/gtmEvents';
import { Breadcrumbs } from './components/Breadcrumbs';

// Types:
import type { ModifierStyles } from '../../domain/state/modifier/types';

// Selectors:
import { getMetadata } from '../../domain/state/metadata/selectors';
import { modifierStylesSelector } from '../../domain/state/modifier/selectors';
import { getLinkedMonths } from '../../domain/state/linked/selectors';

// Context:
import { MobileContext } from '../../domain/context/MobileContext';

const mapStateToProps = (state) => ({
  metadata: getMetadata(state),
  modifierStyles: modifierStylesSelector(state),
  linkedMonths: getLinkedMonths(state),
});

interface HeaderContainerProps {
  metadata: {
    breadcrumbs: Array<{
      text: string;
      link: string;
    }>;
    h1: string;
    short_description: string;
  };
  modifierStyles: ModifierStyles;
  linkedMonths: IMonth[];

  className?: string;
}

export const HeaderContainer = connect(mapStateToProps)(
  ({
    metadata: { breadcrumbs, h1, short_description },
    className = '',
    modifierStyles,
    linkedMonths,
  }: HeaderContainerProps) => {
    const isMobile = useContext(MobileContext);

    return (
      <StyledSection
        className={className}
        $isMobile={isMobile}
        $modifierStyles={modifierStyles}
      >
        {!isEmpty(breadcrumbs) && (
          <Breadcrumbs breadcrumbs={breadcrumbs} isMobile={isMobile} />
        )}
        <StyledTitle $isMobile={isMobile}>{h1}</StyledTitle>
        <StyledDescription $isMobile={isMobile}>
          {short_description}
        </StyledDescription>
        {linkedMonths && (
          <StyledMonthSegments
            // onClick={() => () => {
            //   pushExploreLinkClicked({
            //     custom_params: {
            //       type: 'month',
            //     },
            //   });
            // }}
            months={linkedMonths}
          />
        )}
      </StyledSection>
    );
  },
);

const StyledSection = styled.section<{
  $isMobile: boolean;
  $modifierStyles: ModifierStyles;
}>`
  margin: auto;
  padding: 8px;
  background-color: ${({ $isMobile, $modifierStyles: { backgroundColor } }) =>
    backgroundColor || ($isMobile ? '#e6e6e6' : '#fff')};

  ${({ $modifierStyles: { boldTextColor } }) =>
    `color: ${boldTextColor} !important;`}

  ${({ $modifierStyles: { textColor } }) =>
    textColor &&
    `
    span, p {
      color: ${textColor} !important;
    };`}

  ${({ $modifierStyles: { linkColor } }) =>
    linkColor &&
    `
    a {
      span {
        color: ${linkColor} !important;
      };
    };`}
`;

const StyledTitle = styled.h1<{ $isMobile: boolean }>`
  margin: 3px 0;
  font-size: 28px;
  line-height: 34px;
  font-weight: 700;

  ${({ $isMobile }) =>
    $isMobile &&
    `
    font-size: 18px;
    line-height: 22px;
  `}
`;

const StyledDescription = styled.p<{ $isMobile: boolean }>`
  position: relative;
  font-size: 14px;
  line-height: 16px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.blackLight};

  /* Bottom fade */
  ${({ $isMobile }) =>
    $isMobile &&
    `
    &::after {
      content: '';

      position: absolute;
      bottom: 0;
      left: 0;

      width: 100%;
      height: 21px;

      background: linear-gradient(
        180deg,
        rgba(241, 241, 241, 0.0001) 0%,
        #f1f1f1 100%
      );
    }
  `}
`;

const StyledMonthSegments = styled(MonthSegments)`
  margin: 16px 0 0;
  padding: 0;
  box-shadow: none;
  background-color: transparent;
  border-radius: 4px;
`;
